import { Link } from 'react-router-dom'
import {
  REQUEST_STATUS_LABELS_RU,
  type RequestStatus,
  type UnfInvoiceCandidateDto,
  type UnfInvoicePreviewDto,
} from '@crm/shared'

/** Одной строкой: что совпало у заявки со счётом. */
export function candidateSummary(c: UnfInvoiceCandidateDto): string {
  if (c.blocked) return c.blocked
  const changed = c.lines.filter((l) => l.kind === 'CHANGED').length
  const added = c.lines.filter((l) => l.kind === 'NEW').length
  return [
    `совпало ${c.exact} из ${c.linesTotal}`,
    changed ? `станут как в счёте — ${changed}` : '',
    added ? `нет в заявке — ${added}` : '',
    c.missing.length ? `нет в счёте — ${c.missing.length}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
    className="mt-0.5 shrink-0 text-green-700"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l3 3 5-6" />
  </svg>
)

const WarnIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
    className="mt-0.5 shrink-0 text-amber-700"
  >
    <path d="M12 3l9 16H3z" />
    <path d="M12 10v4" />
    <path d="M12 17h.01" />
  </svg>
)

const Banner = ({
  tone,
  title,
  children,
}: {
  tone: 'green' | 'amber' | 'blue'
  title: string
  children?: React.ReactNode
}) => {
  const cls = {
    green: 'border-green-200 bg-green-50 text-green-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
  }[tone]
  return (
    <div className={`flex gap-2.5 rounded-lg border p-3 text-sm ${cls}`}>
      {tone === 'green' ? <CheckIcon /> : <WarnIcon />}
      <div>
        <div className="font-semibold">{title}</div>
        {children && <div className="mt-0.5">{children}</div>}
      </div>
    </div>
  )
}

/**
 * Итог сверки для выбранной заявки (21.10.2026: заказ подстраивается под счёт):
 * зелёная плашка — сошёлся на 100%; синяя — заказ станет как в счёте; жёлтая —
 * записать нельзя (оплаченный заказ расходится со счётом, заявка не подходит).
 */
export function UnfInvoiceVerdict({
  preview,
  candidate,
}: {
  preview: UnfInvoicePreviewDto
  candidate: UnfInvoiceCandidateDto | null
}) {
  if (!candidate) {
    return (
      <Banner tone="amber" title="Записать некуда">
        {preview.blocked ?? preview.reviewReason ?? 'Подходящая заявка не найдена'}. Счёт можно
        отложить — он появится в «Счетах на проверке».
      </Banner>
    )
  }
  const who = `${candidate.requestNumber ?? '—'}${candidate.managerName ? ` (${candidate.managerName})` : ''}`
  if (candidate.blocked) {
    return (
      <Banner tone="amber" title={`Не записать в заказ по заявке ${who}`}>
        {candidate.blocked}
      </Banner>
    )
  }
  if (candidate.onlyNumber) {
    return candidate.full ? (
      <Banner tone="green" title={`Сошёлся на 100% с заявкой ${who}`}>
        Заказ уже оплачен — в счёт заказа запишутся только номер и дата из 1С.
      </Banner>
    ) : (
      <Banner tone="amber" title="Заказ уже оплачен, а счёт с ним расходится">
        Состав оплаченного заказа программа не меняет: {candidateSummary(candidate)}. Отложите счёт
        на проверку.
      </Banner>
    )
  }
  const twins = preview.candidates.filter((c) => c.full).length > 1
  if (candidate.full) {
    return (
      <Banner tone="green" title={`Сошёлся на 100% с заявкой ${who}`}>
        Все строки ({candidate.linesTotal}) совпали по партномеру, количеству и цене. В заказ
        запишутся номер, дата и сумма счёта.
        {twins && ' Счёт так же совпал с другой заявкой — проверьте, что выбрана нужная.'}
      </Banner>
    )
  }
  return (
    <Banner tone="blue" title={`Заказ по заявке ${who} станет как в счёте`}>
      {candidateSummary(candidate)}. Количество и цена возьмутся из счёта.
    </Banner>
  )
}

/** Подходящие заявки: выбрать, в какую записать счёт; ссылка открывает карточку. */
export function UnfInvoiceCandidates({
  candidates,
  selectedId,
  onSelect,
}: {
  candidates: UnfInvoiceCandidateDto[]
  selectedId: string | null
  onSelect: (requestId: string) => void
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm text-gray-500">В какую заявку записать счёт:</legend>
      {candidates.map((c) => (
        <label
          key={c.requestId}
          className={`flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border p-2 text-sm ${
            c.requestId === selectedId ? 'border-brand-500 bg-brand-50' : 'border-gray-200'
          }`}
        >
          <input
            type="radio"
            name="unf-candidate"
            checked={c.requestId === selectedId}
            disabled={!!c.blocked}
            onChange={() => onSelect(c.requestId)}
          />
          <Link
            to={`/requests/${c.requestId}`}
            target="_blank"
            className="font-medium text-brand-700 hover:underline"
          >
            Заявка {c.requestNumber ?? '—'}
          </Link>
          <span className="text-gray-500">
            {REQUEST_STATUS_LABELS_RU[c.requestStatus as RequestStatus] ?? c.requestStatus}
            {c.managerName ? ` · ${c.managerName}` : ''}
            {c.orderId ? ' · заказ оформлен' : ' · заказа ещё нет'}
          </span>
          <span className="ml-auto text-gray-700">{candidateSummary(c)}</span>
        </label>
      ))}
    </fieldset>
  )
}
